import {
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { map, pipe, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { withToastr } from '@shared/store/toastr.feature';
import { tapResponse } from '@ngrx/operators';
import type { ResetPasswordInput, User } from '@graphql/legacy/types';
import { UserRolesEnum } from '@graphql/legacy/types';
import type { Client } from '@graphql/default/types';
import { mapUserAvatarPath } from '@shared/helpers/map-user-avatar-path';
import { getErrorMessage } from '@shared/helpers/error-message';
import { GetUserGQL, ResetPasswordGQL } from '@graphql/legacy/services';
import { ClientGQL } from '@graphql/default/services';
import { UserService } from '@features/sign-in/services/user.service';
import { ROOT } from '@shared/constants/routing-paths.constants';
import { Router } from '@angular/router';
import { withTreeShakableDevTools } from '@shared/constants/store.features';
import { IS_EMBED_LINK } from '@shared/constants/is-embed-link.constant';
import { isDefined } from '@3dsource/utils';
import { BetaFeaturesStore } from '@features/beta-features/store/beta-features.store';
import { immerPatchState } from 'ngrx-immer/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

interface UserStore {
  user: User | null;
  client: Client | null;
}

const initialState: UserStore = {
  user: null,
  client: null,
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withTreeShakableDevTools('user'),
  withState(initialState),
  withToastr('user'),
  withComputed(({ user, client }) => ({
    userSettings: computed(() => user()?.settings || null),
    userRoles: computed(() => user()?.roles || []),
    userCompany: computed(() => user()?.company || null),
    userCompanyId: computed(() => user()?.company?.id || null),
    isUserSuperAdmin: computed(() =>
      (user()?.roles || [])?.includes(UserRolesEnum.RoleSuperAdmin),
    ),
    isAllowManageMaterials: computed(
      () => user()?.settings?.['allow_manage_materials'] || false,
    ),
    allowRenderPng: computed(
      () => user()?.settings?.['allow_render_png'] || false,
    ),
    allowRenderJpg: computed(
      () => user()?.settings?.['allow_render_jpg'] || false,
    ),
    isAllowRenderImages: computed(
      () =>
        user()?.settings?.['allow_render_png'] ||
        user()?.settings?.['allow_render_jpg'] ||
        false,
    ),
    clientHasPublicAssets: computed(() => client()?.hasPublicAssets),
  })),
  withMethods((store) => ({
    setUser(user: User) {
      immerPatchState(store, { user });
    },
    updateUser(user: User) {
      localStorage.setItem(
        'user',
        JSON.stringify({
          ...JSON.parse(localStorage.getItem('user') ?? ''),
          ...user,
        }),
      );
      immerPatchState(store, (state) => {
        state.user = { ...state.user, ...user };
      });
    },
    setClient(client: Client) {
      immerPatchState(store, { client });
    },
  })),
  withMethods(
    (
      store,
      router = inject(Router),
      userService = inject(UserService),
      betaFeaturesStore = inject(BetaFeaturesStore),
      getUserGQL = inject(GetUserGQL),
      clientGQL = inject(ClientGQL),
      resetPasswordGQL = inject(ResetPasswordGQL),
    ) => {
      const getUser$ = rxMethod<void>(
        pipe(
          filter(() => userService.isLoggedIn),
          switchMap(() =>
            getUserGQL.fetch().pipe(
              tapResponse({
                next: (response) => {
                  const user = mapUserAvatarPath(response.data?.me as User);
                  store.setUser(user);
                  localStorage.setItem('user', JSON.stringify(user));
                  betaFeaturesStore.getBetaFeatures$();
                  getClient$();
                },
                error: (error: unknown) =>
                  store.showErrorToast(getErrorMessage(error).error),
              }),
            ),
          ),
        ),
      );
      const getClient$ = rxMethod<void>(
        pipe(
          switchMap(() =>
            clientGQL
              .fetch({ input: { clientId: store.userCompanyId() || '' } })
              .pipe(
                tapResponse({
                  next: (response) =>
                    store.setClient(response.data.client as Client),
                  error: (error: unknown) =>
                    store.showErrorToast(getErrorMessage(error).error),
                }),
              ),
          ),
        ),
      );
      const getUserFromLocalStorage$ = rxMethod<void>(
        pipe(
          filter(() => !IS_EMBED_LINK),
          map(() => {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
          }),
          tap((user) => {
            if (isDefined(user)) {
              store.setUser(mapUserAvatarPath(user as User));
              getClient$();
            } else {
              getUser$();
            }
          }),
        ),
      );
      const resetPassword$ = rxMethod<{ input: ResetPasswordInput }>(
        pipe(
          switchMap(({ input }) =>
            resetPasswordGQL.mutate({ input }).pipe(
              tapResponse({
                next: () => {
                  store.showSuccessToast(
                    'Password was successfully reset',
                    'Reset Password',
                  );
                  void router.navigate([ROOT]);
                },
                error: (error: unknown) =>
                  store.showErrorToast(
                    getErrorMessage(error).error,
                    'Reset Password',
                  ),
              }),
            ),
          ),
        ),
      );
      return { getUser$, getClient$, getUserFromLocalStorage$, resetPassword$ };
    },
  ),
);
